import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  X,
  ExternalLink,
  MessageSquare,
  Copy,
  Check,
  AlertCircle,
  FileText,
  User,
  Send,
  Phone,
  Mail,
  Calendar,
  ChevronDown,
  Code
} from "lucide-react";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";

import DisparoWhatsAppPreview from "./DisparoWhatsAppPreview";
import DisparoEmailPreview from "./DisparoEmailPreview";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  dialogContent: {
    padding: theme.spacing(3),
    maxHeight: "75vh"
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2)
  },
  firstSectionTitle: {
    marginTop: 0
  },
  infoBox: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`
  },
  infoLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textTransform: "uppercase"
  },
  infoValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    wordBreak: "break-word"
  },
  errorBanner: {
    backgroundColor: "rgba(220, 38, 38, 0.1)",
    border: "1px solid rgba(220, 38, 38, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  accordionRoot: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px !important",
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    boxShadow: "none",
    "&:before": {
      display: "none"
    }
  },
  accordionSummary: {
    padding: theme.spacing(0, 2),
    minHeight: 46,
    "&.Mui-expanded": {
      minHeight: 46
    }
  },
  accordionDetails: {
    padding: theme.spacing(1, 2, 2, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "block"
  },
  jsonContainer: {
    backgroundColor: "#1e1e1e",
    color: "#d4d4d4",
    padding: theme.spacing(1.5),
    borderRadius: 6,
    fontFamily: "monospace",
    fontSize: "0.8rem",
    maxHeight: 200,
    overflowY: "auto",
    position: "relative"
  }
}));

const DisparoDetalhesModal = ({ open, onClose, disparo }) => {
  const classes = useStyles();
  const history = useHistory();
  const [copied, setCopied] = useState(false);

  if (!disparo) return null;

  const formatarDataBR = (dataStr) => {
    if (!dataStr) return "—";
    if (typeof dataStr !== "string") return String(dataStr);
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dataStr)) {
      return dataStr;
    }
    const match = dataStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
    try {
      const parsed = new Date(dataStr);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "dd/MM/yyyy");
      }
    } catch (e) {}
    return dataStr;
  };

  const formatarDataHora = (dataStr) => {
    if (!dataStr) return "—";
    if (typeof dataStr !== "string") return String(dataStr);
    if (/^\d{2}\/\d{2}\/\d{4}/.test(dataStr)) {
      return dataStr;
    }
    try {
      const parsed = new Date(dataStr);
      if (!isNaN(parsed.getTime())) {
        return format(parsed, "dd/MM/yyyy HH:mm:ss");
      }
    } catch (e) {}
    return dataStr;
  };

  const handleCopyJson = () => {
    if (disparo.payload_envio) {
      navigator.clipboard.writeText(JSON.stringify(disparo.payload_envio, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGoToTicket = () => {
    if (disparo.ticket_id) {
      onClose();
      history.push(`/tickets/${disparo.ticket_id}`);
    }
  };

  const rawStatusLabel = disparo.status_envio_label || disparo.status_envio || "";
  const formattedStatusLabel = rawStatusLabel
    ? rawStatusLabel.charAt(0).toUpperCase() + rawStatusLabel.slice(1)
    : "—";
  const isCanceled =
    disparo.status_envio === "cancelado" ||
    rawStatusLabel.toLowerCase().includes("cancelado");
  const isSuccess =
    disparo.status_envio === "sucesso" ||
    rawStatusLabel.toLowerCase().includes("sucesso");

  const canalLower = String(disparo.canal || "").toLowerCase();
  const isEmail =
    canalLower.includes("email") ||
    Boolean(disparo.motivo_fallback) ||
    Boolean(disparo.payload_envio?.detalhes_email);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Detalhes do Disparo RPA
          </Typography>
          <Chip
            size="small"
            label={formattedStatusLabel}
            style={{
              backgroundColor: isSuccess ? "#16a34a" : isCanceled ? "#ea580c" : "#dc2626",
              color: "#fff",
              fontWeight: 600,
              marginLeft: 8
            }}
          />
        </Box>
        <IconButton size="small" onClick={onClose}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {/* Banner de Aviso / Inconsistência */}
        {(!isSuccess || disparo.erro_detalhes || disparo.motivo_fallback) && (
          <Box
            className={classes.errorBanner}
            style={
              isCanceled
                ? { backgroundColor: "rgba(234, 88, 12, 0.1)", border: "1px solid rgba(234, 88, 12, 0.3)" }
                : undefined
            }
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              color={isCanceled ? "#c2410c" : "#dc2626"}
              mb={1}
            >
              <AlertCircle size={20} />
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                {isCanceled
                  ? "Informação: Disparo cancelado pelo sistema"
                  : "Atenção: Ocorreu uma inconsistência no envio"}
              </Typography>
            </Box>
            {disparo.erro_detalhes && (
              <Typography
                variant="body2"
                style={{ color: isCanceled ? "#9a3412" : "#b91c1c", wordBreak: "break-word" }}
              >
                <strong>Motivo / Detalhe:</strong> {disparo.erro_detalhes}
              </Typography>
            )}
            {disparo.motivo_fallback && (
              <Typography
                variant="body2"
                style={{ color: isCanceled ? "#9a3412" : "#b91c1c", marginTop: 4 }}
              >
                <strong>Motivo de contingência/fallback:</strong> {disparo.motivo_fallback}
              </Typography>
            )}
          </Box>
        )}

        {/* 1. Dados do Colaborador */}
        <Typography className={`${classes.sectionTitle} ${classes.firstSectionTitle}`}>
          <User size={18} /> Dados do Colaborador (ERP GiSign / Ícarus)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Nome Completo</Typography>
              <Typography className={classes.infoValue}>
                {disparo.nome_funcionario || "Não informado"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>CPF</Typography>
              <Typography className={classes.infoValue}>
                {disparo.cpf_formatado || disparo.cpf || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Matrícula Ginfor</Typography>
              <Typography className={classes.infoValue}>
                {disparo.matricula_ginfor || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Telefone / WhatsApp</Typography>
              <Typography className={classes.infoValue}>
                {disparo.celular || disparo.destinatario || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>E-mail</Typography>
              <Typography className={classes.infoValue}>
                {disparo.email || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Data de Admissão</Typography>
              <Typography className={classes.infoValue}>
                {formatarDataBR(disparo.data_admissao)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 2. Informações do Disparo */}
        <Typography className={classes.sectionTitle}>
          <Send size={18} /> Informações do Disparo & Automação
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Etapa / Tipo de Mensagem</Typography>
              <Typography className={classes.infoValue}>
                {disparo.tipo_mensagem_label || disparo.tipo_mensagem || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Template Meta WABA</Typography>
              <Typography className={classes.infoValue}>
                {disparo.template_name || "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Canal & Tentativa</Typography>
              <Typography className={classes.infoValue}>
                {disparo.canal_label || disparo.canal} (Tentativa #{disparo.tentativa_numero || 1})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Data / Hora do Evento</Typography>
              <Typography className={classes.infoValue}>
                {formatarDataHora(disparo.data_hora_formatada || disparo.created_at)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Destinatário do Envio</Typography>
              <Typography className={classes.infoValue}>
                {disparo.destinatario || "—"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 3. Integração CRM & Envelope GiSign */}
        <Typography className={classes.sectionTitle}>
          <FileText size={18} /> Vínculo GiSign & Atendimento CRM
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Ticket no CRM</Typography>
              <Typography className={classes.infoValue}>
                {disparo.ticket_id ? `#${disparo.ticket_id}` : "Não criado / Vinculado"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Contato ID no CRM</Typography>
              <Typography className={classes.infoValue}>
                {disparo.contact_id ? `#${disparo.contact_id}` : "—"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box className={classes.infoBox}>
              <Typography className={classes.infoLabel}>Status Envelope GiSign</Typography>
              <Typography className={classes.infoValue}>
                {disparo.status_envelope || "—"}
              </Typography>
            </Box>
          </Grid>
          {disparo.wamid && (
            <Grid item xs={12}>
              <Box className={classes.infoBox}>
                <Typography className={classes.infoLabel}>WAMID (Identificador Meta)</Typography>
                <Typography className={classes.infoValue} style={{ fontSize: "0.8rem" }}>
                  {disparo.wamid}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* 4. Pré-visualização da Mensagem Enviada */}
        <Typography className={classes.sectionTitle}>
          <Send size={18} /> Pré-visualização da Mensagem Enviada
        </Typography>

        {isEmail ? (
          <DisparoEmailPreview disparo={disparo} />
        ) : (
          <DisparoWhatsAppPreview disparo={disparo} />
        )}

        {/* 5. Dados Técnicos do Robô (Auditoria JSON) */}
        {disparo.payload_envio && (
          <Accordion elevation={0} className={classes.accordionRoot}>
            <AccordionSummary
              expandIcon={<ChevronDown size={18} />}
              className={classes.accordionSummary}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Code size={16} />
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Dados Técnicos do Disparo (Auditoria JSON)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="caption" style={{ color: "#888" }}>
                    Registro bruto retornado pelos logs do robô
                  </Typography>
                  <Button
                    size="small"
                    startIcon={copied ? <Check size={16} /> : <Copy size={16} />}
                    onClick={handleCopyJson}
                    style={{ textTransform: "none", color: copied ? "#16a34a" : undefined }}
                  >
                    {copied ? "Copiado!" : "Copiar JSON"}
                  </Button>
                </Box>
                <pre className={classes.jsonContainer}>
                  {JSON.stringify(disparo.payload_envio, null, 2)}
                </pre>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "12px 24px" }}>
        {disparo.link_assinatura && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExternalLink size={16} />}
            href={disparo.link_assinatura}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir Link de Assinatura
          </Button>
        )}

        {disparo.ticket_id && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<MessageSquare size={16} />}
            onClick={handleGoToTicket}
          >
            Ver Conversa no CRM
          </Button>
        )}

        <Button onClick={onClose} color="default">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisparoDetalhesModal;
